import { supabase } from '../supabaseClient';
import { throttleSupabaseQuery } from '../../utils/requestThrottler';

export class PathwayService {
  // IMMIGRATION PATHWAYS MANAGEMENT
  static async getImmigrationPathways(countryId) {
    try {
      const { data, error } = await supabase
        .from('immigration_pathways')
        .select(`
          *,
          current_version:current_version_id (
            id,
            version_number,
            status,
            change_notes,
            published_at,
            content
          ),
          countries(name, flag_emoji),
          cost_components(*),
          scoring_criteria(*)
        `)
        .eq('country_id', countryId)
        .order('sort_order');

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error fetching immigration pathways:', error);
      throw error;
    }
  }

  static async createPathwayDraft(pathwayId, { authorId = null, changeNotes = null, baseContent = {} } = {}) {
    try {
      const { data: latest, error: latestError } = await supabase
        .from('pathway_versions')
        .select('version_number')
        .eq('pathway_id', pathwayId)
        .order('version_number', { ascending: false })
        .limit(1)
        .single();

      if (latestError && latestError.code !== 'PGRST116') {
        throw latestError;
      }

      const nextVersion = (latest?.version_number || 0) + 1;

      const payload = {
        pathway_id: pathwayId,
        version_number: nextVersion,
        status: 'draft',
        title: baseContent.pathway_key || baseContent.slug || `Pathway draft ${nextVersion}`,
        content: baseContent,
        change_notes: changeNotes,
        created_by: authorId
      };

      const { data, error } = await supabase
        .from('pathway_versions')
        .insert([payload])
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating pathway draft:', error);
      throw error;
    }
  }

  static async getPathwayVersions(pathwayId) {
    try {
      const { data, error } = await supabase
        .from('pathway_versions')
        .select('*')
        .eq('pathway_id', pathwayId)
        .order('version_number', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching pathway versions:', error);
      throw error;
    }
  }

  static async updatePathwayVersion(versionId, updates) {
    try {
      const { data, error } = await supabase
        .from('pathway_versions')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', versionId)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating pathway version:', error);
      throw error;
    }
  }

  static async publishPathwayVersion(versionId, pathwayId, changeNotes = null) {
    try {
      const payload = {
        status: 'published',
        published_at: new Date().toISOString(),
        change_notes: changeNotes || null
      };

      const { data, error } = await supabase
        .from('pathway_versions')
        .update(payload)
        .eq('id', versionId)
        .select()
        .single();

      if (error) throw error;

      const { error: pathwayError } = await supabase
        .from('immigration_pathways')
        .update({ current_version_id: versionId, updated_at: new Date().toISOString() })
        .eq('id', pathwayId);

      if (pathwayError) throw pathwayError;

      return data;
    } catch (error) {
      console.error('Error publishing pathway version:', error);
      throw error;
    }
  }

  static async clonePathwayVersion(versionId) {
    try {
      // Fetch source version
      const { data: src, error: srcError } = await supabase
        .from('pathway_versions')
        .select('*')
        .eq('id', versionId)
        .single();

      if (srcError) throw srcError;
      const pathwayId = src.pathway_id;

      // Determine next version number for this pathway
      const { data: latest, error: latestError } = await supabase
        .from('pathway_versions')
        .select('version_number')
        .eq('pathway_id', pathwayId)
        .order('version_number', { ascending: false })
        .limit(1)
        .single();

      if (latestError && latestError.code !== 'PGRST116') {
        throw latestError;
      }

      const nextVersion = (latest?.version_number || 0) + 1;

      const payload = {
        pathway_id: pathwayId,
        version_number: nextVersion,
        status: 'draft',
        title: src.title || `Pathway draft ${nextVersion}`,
        summary: src.summary || null,
        content: src.content || {},
        eligibility_rules: src.eligibility_rules || [],
        cost_model: src.cost_model || null,
        change_notes: `Cloned from v${src.version_number}`,
      };

      const { data, error } = await supabase
        .from('pathway_versions')
        .insert([payload])
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error cloning pathway version:', error);
      throw error;
    }
  }

  static async createPathway(pathwayData) {
    try {
      const { data, error } = await supabase
        .from('immigration_pathways')
        .insert([pathwayData])
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating pathway:', error);
      throw error;
    }
  }

  static async updatePathway(id, updates) {
    try {
      const { data, error } = await supabase
        .from('immigration_pathways')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating pathway:', error);
      throw error;
    }
  }

  static async deletePathway(id) {
    try {
      const { error } = await supabase
        .from('immigration_pathways')
        .delete()
        .eq('id', id);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error deleting pathway:', error);
      throw error;
    }
  }

  static async bulkSetPathwaysActive(ids = [], isActive = true) {
    if (!Array.isArray(ids) || ids.length === 0) return [];
    try {
      const { data, error } = await supabase
        .from('immigration_pathways')
        .update({ is_active: !!isActive, updated_at: new Date().toISOString() })
        .in('id', ids)
        .select();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error bulk updating pathways active state:', error);
      throw error;
    }
  }

  // COST COMPONENTS MANAGEMENT
  static async getCostComponents(pathwayId) {
    try {
      const { data, error } = await supabase
        .from('cost_components')
        .select('*')
        .eq('pathway_id', pathwayId)
        .order('is_mandatory', { ascending: false });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error fetching cost components:', error);
      throw error;
    }
  }

  static async createCostComponent(componentData) {
    try {
      const { data, error } = await supabase
        .from('cost_components')
        .insert([componentData])
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating cost component:', error);
      throw error;
    }
  }

  static async updateCostComponent(id, updates) {
    try {
      const { data, error } = await supabase
        .from('cost_components')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating cost component:', error);
      throw error;
    }
  }

  static async deleteCostComponent(id) {
    try {
      const { error } = await supabase
        .from('cost_components')
        .delete()
        .eq('id', id);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error deleting cost component:', error);
      throw error;
    }
  }

  // SCORING CRITERIA MANAGEMENT
  static async getScoringCriteria(pathwayId) {
    try {
      const { data, error } = await supabase
        .from('scoring_criteria')
        .select(`
          *,
          scoring_rules(*)
        `)
        .eq('pathway_id', pathwayId)
        .order('sort_order');

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error fetching scoring criteria:', error);
      throw error;
    }
  }

  static async createScoringCriteria(criteriaData) {
    try {
      const { data, error } = await supabase
        .from('scoring_criteria')
        .insert([criteriaData])
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating scoring criteria:', error);
      throw error;
    }
  }

  static async updateScoringCriteria(id, updates) {
    try {
      const { data, error } = await supabase
        .from('scoring_criteria')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating scoring criteria:', error);
      throw error;
    }
  }

  static async deleteScoringCriteria(id) {
    try {
      const { error } = await supabase
        .from('scoring_criteria')
        .delete()
        .eq('id', id);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error deleting scoring criteria:', error);
      throw error;
    }
  }

  // SCORING RULES MANAGEMENT
  static async createScoringRule(ruleData) {
    try {
      const { data, error } = await supabase
        .from('scoring_rules')
        .insert([ruleData])
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating scoring rule:', error);
      throw error;
    }
  }

  static async updateScoringRule(id, updates) {
    try {
      const { data, error } = await supabase
        .from('scoring_rules')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating scoring rule:', error);
      throw error;
    }
  }

  static async deleteScoringRule(id) {
    try {
      const { error } = await supabase
        .from('scoring_rules')
        .delete()
        .eq('id', id);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error deleting scoring rule:', error);
      throw error;
    }
  }

  // PACKAGES MANAGEMENT
  static async getPackages(pathwayId) {
    try {
      const { data, error } = await supabase
        .from('packages')
        .select(`
          *,
          package_perks(*)
        `)
        .eq('pathway_id', pathwayId)
        .order('sort_order');

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error fetching packages:', error);
      throw error;
    }
  }

  static async createPackage(packageData) {
    try {
      const { data, error } = await supabase
        .from('packages')
        .insert([packageData])
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating package:', error);
      throw error;
    }
  }

  static async updatePackage(id, updates) {
    try {
      const { data, error } = await supabase
        .from('packages')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating package:', error);
      throw error;
    }
  }

  static async deletePackage(id) {
    try {
      const { error } = await supabase
        .from('packages')
        .delete()
        .eq('id', id);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error deleting package:', error);
      throw error;
    }
  }

  static async createPackagePerk(perkData) {
    try {
      const { data, error } = await supabase
        .from('package_perks')
        .insert([perkData])
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating package perk:', error);
      throw error;
    }
  }

  static async updatePackagePerk(id, updates) {
    try {
      const { data, error } = await supabase
        .from('package_perks')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating package perk:', error);
      throw error;
    }
  }

  static async deletePackagePerk(id) {
    try {
      const { error } = await supabase
        .from('package_perks')
        .delete()
        .eq('id', id);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error deleting package perk:', error);
      throw error;
    }
  }

  // LOAN OPTIONS MANAGEMENT
  static async getLoanOptions(pathwayId) {
    try {
      const { data, error } = await supabase
        .from('loan_options')
        .select('*')
        .eq('pathway_id', pathwayId)
        .order('interest_rate');

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error fetching loan options:', error);
      throw error;
    }
  }

  static async createLoanOption(loanData) {
    try {
      const { data, error } = await supabase
        .from('loan_options')
        .insert([loanData])
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating loan option:', error);
      throw error;
    }
  }

  static async updateLoanOption(id, updates) {
    try {
      const { data, error } = await supabase
        .from('loan_options')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating loan option:', error);
      throw error;
    }
  }

  static async deleteLoanOption(id) {
    try {
      const { error } = await supabase
        .from('loan_options')
        .delete()
        .eq('id', id);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error deleting loan option:', error);
      throw error;
    }
  }

  // Throttled version of getImmigrationPathways
  static async getImmigrationPathwaysThrottled(countryId) {
    return throttleSupabaseQuery(
      async () => {
        const { data, error } = await supabase
          .from('immigration_pathways')
          .select(`
            *,
            countries(name, flag_emoji),
            cost_components(*),
            scoring_criteria(*)
          `)
          .eq('country_id', countryId)
          .order('sort_order');

        if (error) throw error;
        return data;
      },
      `get_immigration_pathways_${countryId}`,
      {
        retries: 2,
        useCache: true,
        cacheTimeout: 2 * 60 * 1000 // 2 minutes
      }
    );
  }

  // Throttled version of getPackages
  static async getPackagesThrottled(pathwayId) {
    return throttleSupabaseQuery(
      async () => {
        const { data, error } = await supabase
          .from('packages')
          .select(`
            *,
            package_perks(*)
          `)
          .eq('pathway_id', pathwayId)
          .order('sort_order');

        if (error) throw error;
        return data;
      },
      `get_packages_${pathwayId}`,
      {
        retries: 2,
        useCache: true,
        cacheTimeout: 2 * 60 * 1000 // 2 minutes
      }
    );
  }

  // Throttled version of getLoanOptions
  static async getLoanOptionsThrottled(pathwayId) {
    return throttleSupabaseQuery(
      async () => {
        const { data, error } = await supabase
          .from('loan_options')
          .select('*')
          .eq('pathway_id', pathwayId)
          .order('interest_rate');

        if (error) throw error;
        return data;
      },
      `get_loan_options_${pathwayId}`,
      {
        retries: 2,
        useCache: true,
        cacheTimeout: 2 * 60 * 1000 // 2 minutes
      }
    );
  }
}
